// TrailMap — interactive Leaflet map for the /trails page.
//
// Each trail is rendered as a brass medallion pin (same badge as the nav/footer).
// Popups show distance, climb, difficulty, adaptive-friendliness and the founder's
// personal note, plus a "Get directions" link.
//
// We manage Leaflet imperatively (not via react-leaflet) because React 18
// StrictMode's double-mount cycle triggers "Map container is already initialized"
// with react-leaflet's declarative wrapper. Imperative init + `map.remove()` on
// cleanup handles StrictMode cleanly.
//
// Uses OpenStreetMap tiles — no API key, no fees, no vendor lock-in.

import { useEffect, useMemo, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import { DIFFICULTY_COLOURS } from '@/data/trails';
import { TRAILS } from '@/constants/testIds';

// Cache-busted medallion (matches BrandLogo's MEDALLION_VERSION).
const MEDALLION_URL = '/brand/medallion.png?v=3';
const MARKER_SIZE = 44;

function buildMedallionIcon(difficulty) {
  const colour = DIFFICULTY_COLOURS[difficulty] || '#4A5D23';
  return L.divIcon({
    className: 'trail-pin',
    iconSize: [MARKER_SIZE, MARKER_SIZE + 12],
    iconAnchor: [MARKER_SIZE / 2, MARKER_SIZE + 12],
    popupAnchor: [0, -(MARKER_SIZE + 8)],
    html: `
      <div style="position:relative;width:${MARKER_SIZE}px;height:${MARKER_SIZE + 12}px;">
        <img src="${MEDALLION_URL}" alt=""
             style="display:block;width:${MARKER_SIZE}px;height:${MARKER_SIZE}px;max-width:${MARKER_SIZE}px;border-radius:50%;box-shadow:0 6px 12px -4px rgba(0,0,0,0.55);" />
        <span style="position:absolute;left:50%;bottom:0;transform:translateX(-50%);width:10px;height:10px;background:${colour};box-shadow:0 2px 4px rgba(0,0,0,0.4);border-radius:50%;border:2px solid #F4F1EA"></span>
      </div>`,
  });
}

function buildPopupHtml(t) {
  const adaptivePill = t.adaptiveFriendly
    ? `<div style="display:inline-block;font-size:9px;letter-spacing:0.16em;font-weight:600;text-transform:uppercase;color:#228B22;background:rgba(197,179,88,0.10);border:1px solid #C5B358;padding:2px 6px;margin-bottom:6px;">Adaptive-friendly</div>`
    : '';
  return `
    <div data-testid="${TRAILS.trailPopup(t.slug)}" style="min-width:220px;">
      <div style="font-size:10px;letter-spacing:0.22em;text-transform:uppercase;font-weight:600;color:#4A5D23;margin-bottom:4px;">${t.region}</div>
      <h4 style="font-family:'Cormorant Garamond',serif;font-weight:600;font-size:18px;color:#0E3D14;line-height:1.15;margin:0 0 8px;">${t.name}</h4>
      <div style="display:flex;gap:8px;font-size:12px;color:#333;margin-bottom:6px;align-items:center;">
        <span>${t.distance_km} km</span><span>·</span>
        <span>${t.climb_m} m climb</span><span>·</span>
        <span style="color:${DIFFICULTY_COLOURS[t.difficulty] || '#4A5D23'};font-weight:700;">${t.difficulty}</span>
      </div>
      <div style="font-size:12px;color:#555;margin-bottom:8px;"><em>Surface:</em> ${t.surface}</div>
      ${adaptivePill}
      <p style="font-size:12px;font-style:italic;color:#333;line-height:1.45;border-left:2px solid #C5B358;padding-left:8px;margin:6px 0;">&ldquo;${t.note}&rdquo;</p>
      <a href="${t.external}" target="_blank" rel="noopener noreferrer" style="display:inline-block;font-size:11px;font-weight:600;letter-spacing:0.18em;text-transform:uppercase;color:#228B22;text-decoration:underline;text-decoration-color:#C5B358;text-underline-offset:4px;">Get directions →</a>
    </div>
  `;
}

export default function TrailMap({ trails, adaptiveOnly, onSelect }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);

  const visible = useMemo(
    () => (adaptiveOnly ? trails.filter((t) => t.adaptiveFriendly) : trails),
    [trails, adaptiveOnly]
  );

  // Init the map ONCE per mount, tear it down on unmount. StrictMode double-mount
  // is safe because `map.remove()` fully unhooks Leaflet from the DOM.
  useEffect(() => {
    const el = containerRef.current;
    if (!el || mapRef.current) return undefined;

    const map = L.map(el, {
      center: [56.7, -4.2],
      zoom: 6,
      scrollWheelZoom: false,
      zoomControl: true,
    });
    mapRef.current = map;

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 18,
    }).addTo(map);

    return () => {
      markersRef.current.forEach((m) => m.remove());
      markersRef.current = [];
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // Re-draw markers whenever the visible set changes (adaptiveOnly toggle).
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    // Clear existing markers
    markersRef.current.forEach((m) => m.remove());
    markersRef.current = [];

    visible.forEach((t) => {
      const marker = L.marker(t.coords, {
        icon: buildMedallionIcon(t.difficulty),
        title: t.name,
      }).addTo(map);
      marker.bindPopup(buildPopupHtml(t), { maxWidth: 280 });
      marker.on('click', () => onSelect?.(t.slug));
      markersRef.current.push(marker);
    });
  }, [visible, onSelect]);

  return (
    <div
      data-testid={TRAILS.map}
      className="w-full h-[480px] sm:h-[560px] border border-[var(--brand-stone)] relative overflow-hidden"
    >
      <div
        ref={containerRef}
        className="w-full h-full"
        style={{ background: '#e6e2d5' }}
      />
    </div>
  );
}
